/**
 * hubResolve.ts
 *
 * Pure client-side resolvers for Hub preview data.
 * Ported 1:1 from editor-api hub_preview_resolvers.js, hub_mods_preview.js,
 * hub_mods_shared.js — variable/icon/tag/branch id→value lookups using
 * maps already available in editor-client.
 *
 * All functions are pure (no side-effects) and are tested independently.
 */

// ---------------------------------------------------------------------------
// Shared map types
// ---------------------------------------------------------------------------

export type IconMap = Record<number, string | null>; // iconId → url

// Resolved tag template shape stored in tagTemplateMap.
// icon is resolved from iconMap at build time (mirrors _buildHubTagTemplateMap:131 on server).
// iconId is kept so downstream code can still read the raw id if needed.
export type TagTemplate = {
  id: number;
  iconId?: number | null;
  icon: string | null;
  [key: string]: unknown; // other server fields forwarded as-is
};
export type TagTemplateMap = Record<number, TagTemplate>; // tagTemplateId → resolved template

export type VariableMap = Record<number, string>; // variableId → name
// Server stores branch.title as-is (null for untitled branches).
// Client must match: acc[id] = title ?? null, NOT title ?? '' — otherwise the
// ?? branchId fallback in _resolveBranchTitle never fires for null-title branches.
export type BranchMap = Record<number, string | null>; // branchId → title (null when untitled)

export type HubResolveMaps = {
  iconMap: IconMap;
  tagTemplateMap: TagTemplateMap;
  variableMap: VariableMap;
  branchMap: BranchMap;
};

// ---------------------------------------------------------------------------
// resolveTags — port of hub_preview_resolvers.js resolveTagsForPreview
// ---------------------------------------------------------------------------

export function resolveTags(
  tags: { top?: unknown[]; bottom?: unknown[] } | null | undefined,
  tagTemplateMap: TagTemplateMap,
): { top: unknown[]; bottom: unknown[] } | null {
  if (!tags) return null;

  const resolveEntries = (entries: unknown[] | undefined): unknown[] => {
    if (!Array.isArray(entries) || entries.length === 0) return entries ?? [];
    if (!tagTemplateMap) return entries;
    return entries.map((entry) => {
      const { tagTemplateId, sortOrder } = entry as { tagTemplateId: number; sortOrder: number };
      const template = tagTemplateMap[tagTemplateId];
      if (!template) return { tagTemplateId, sortOrder };
      return { ...template, sortOrder };
    });
  };

  return { top: resolveEntries(tags.top), bottom: resolveEntries(tags.bottom) };
}

// ---------------------------------------------------------------------------
// resolveScore — port of resolveScoreForPreview + _resolveScoreSide
// ---------------------------------------------------------------------------

type ScoreSideResolved =
  | { type: 'literal'; value: unknown }
  | { type: 'variable'; variable: string | null };

function _resolveScoreSide(
  side: { type?: string; value?: number | null } | null | undefined,
  variableMap: VariableMap,
): ScoreSideResolved {
  if (!side) return { type: 'literal', value: null };
  if (side.type === 'variable') {
    const name = side.value != null ? (variableMap[side.value] ?? null) : null;
    return { type: 'variable', variable: name };
  }
  return { type: 'literal', value: side.value ?? null };
}

export function resolveScore(
  score: { numerator?: unknown; denominator?: unknown } | null | undefined,
  variableMap: VariableMap,
): { numerator: ScoreSideResolved; denominator: ScoreSideResolved } | null {
  if (!score) return null;
  return {
    numerator: _resolveScoreSide(score.numerator as Parameters<typeof _resolveScoreSide>[0], variableMap),
    denominator: _resolveScoreSide(score.denominator as Parameters<typeof _resolveScoreSide>[0], variableMap),
  };
}

// ---------------------------------------------------------------------------
// resolveProgressBars — port of serializePreviewProgressBars + helpers
// ---------------------------------------------------------------------------

type ProgressBarSideResolved =
  | { type: 'literal'; value: unknown }
  | { type: 'variable'; variable: string | null };

function _serializeProgressBarSide(
  type: string | undefined,
  literal: unknown,
  variableId: number | null | undefined,
  variableMap: VariableMap,
): ProgressBarSideResolved {
  if (type === 'variable') {
    const name = variableId != null ? (variableMap[variableId] ?? null) : null;
    return { type: 'variable', variable: name };
  }
  return { type: 'literal', value: literal ?? null };
}

function _serializeProgressBar(
  bar: Record<string, unknown>,
  variableMap: VariableMap,
): { label: unknown; numerator: ProgressBarSideResolved; denominator: ProgressBarSideResolved } {
  const b = (typeof bar.toJSON === 'function' ? bar.toJSON() : bar) as Record<string, unknown>;
  return {
    label: b.label,
    numerator: _serializeProgressBarSide(
      b.numeratorType as string | undefined,
      b.numeratorLiteral,
      b.numeratorVariableId as number | null | undefined,
      variableMap,
    ),
    denominator: _serializeProgressBarSide(
      b.denominatorType as string | undefined,
      b.denominatorLiteral,
      b.denominatorVariableId as number | null | undefined,
      variableMap,
    ),
  };
}

export function resolveProgressBars(
  progressBars: unknown[] | null | undefined,
  variableMap: VariableMap,
): unknown[] | null {
  if (!Array.isArray(progressBars) || progressBars.length === 0) return null;
  return progressBars
    .slice()
    .sort((a, b) => ((a as Record<string, unknown>).sortOrder as number) - ((b as Record<string, unknown>).sortOrder as number))
    .map((bar) => _serializeProgressBar(bar as Record<string, unknown>, variableMap));
}

// ---------------------------------------------------------------------------
// resolveMods — port of hub_mods_preview.js + hub_mods_shared.js
// CRITICAL: missing variable fallback → RAW variableId (NOT null)
// ---------------------------------------------------------------------------

function resolveIconVal(
  val: Record<string, unknown>,
  iconMap: IconMap,
): Record<string, unknown> {
  const { iconId, ...rest } = val;
  const icon = iconId ? (iconMap[iconId as number] ?? null) : null;
  return { ...rest, icon };
}

function resolveTagVal(
  val: Record<string, unknown>,
  tagTemplateMap: TagTemplateMap,
): Record<string, unknown> {
  if (!tagTemplateMap || !val.tagTemplateId) return val;
  const template = tagTemplateMap[val.tagTemplateId as number];
  if (!template) return val;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { tagTemplateId, ...rest } = val;
  return { ...rest, ...template };
}

function _resolveGotoValAsBranchId(val: Record<string, unknown>): Record<string, unknown> {
  const { gotoBranchId, ...rest } = val;
  return { ...rest, gotoBranchId: gotoBranchId ?? null };
}

function _resolveRuleValue(
  value: unknown,
  variableMap: VariableMap,
): unknown {
  if (value && typeof value === 'object' && (value as Record<string, unknown>).type === 'variable') {
    const { variableId, ...rest } = value as Record<string, unknown>;
    // Fallback-asymmetry: missing variable → RAW variableId (NOT null)
    return { ...rest, variable: variableMap[variableId as number] ?? variableId };
  }
  return value;
}

// resolveWhen and resolveRule are mutually recursive (nested condition groups).
// Both are declared as arrow functions assigned to const to satisfy no-use-before-define:
// arrow function bodies are evaluated lazily so forward reference is safe at call time.
// eslint-disable-next-line prefer-const
let resolveRule: (rule: Record<string, unknown>, variableMap: VariableMap) => Record<string, unknown>;

export function resolveWhen(
  group: Record<string, unknown>,
  variableMap: VariableMap,
): Record<string, unknown> {
  return {
    match: group.match,
    rules: (group.rules as Record<string, unknown>[]).map((rule) => resolveRule(rule, variableMap)),
  };
}

// eslint-disable-next-line prefer-const
resolveRule = (
  rule: Record<string, unknown>,
  variableMap: VariableMap,
): Record<string, unknown> => {
  if (rule.match !== undefined) return resolveWhen(rule, variableMap);
  const { variableId, value, ...rest } = rule;
  return {
    ...rest,
    variable: variableMap[variableId as number] ?? variableId, // fallback → RAW variableId
    value: _resolveRuleValue(value, variableMap),
  };
};

function _resolveVal(
  prop: string,
  val: Record<string, unknown>,
  ctx: HubResolveMaps,
): unknown {
  switch (prop) {
    case 'goto':
    case 'replay.goto':
      return _resolveGotoValAsBranchId(val);
    case 'icon':
    case 'replay.icon':
      return resolveIconVal(val, ctx.iconMap);
    case 'tags.top':
    case 'tags.bottom':
      return resolveTagVal(val, ctx.tagTemplateMap);
    default:
      return val;
  }
}

export function resolveMods(
  mods: Array<{ prop: string; val: Record<string, unknown>; when: Record<string, unknown>; sortOrder: number }> | null | undefined,
  ctx: HubResolveMaps,
): Record<string, Array<{ val: unknown; when: Record<string, unknown> }>> {
  if (!Array.isArray(mods) || mods.length === 0) return {};
  return mods
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .reduce<Record<string, Array<{ val: unknown; when: Record<string, unknown> }>>>((acc, mod) => {
      const { prop, val, when } = mod;
      if (!acc[prop]) acc[prop] = [];
      acc[prop].push({
        val: _resolveVal(prop, val, ctx),
        when: resolveWhen(when, ctx.variableMap),
      });
      return acc;
    }, {});
}

// ---------------------------------------------------------------------------
// resolveReplay — port of _resolveReplayForPreview + _resolveBranchTitle
// ---------------------------------------------------------------------------

// Exported so transformNodeToBranchDetails can resolve gotoBranchTitle for hub options,
// mirroring public_preview.js:305 which resolves from branchMap server-side.
export function _resolveBranchTitle(
  branchId: number | null | undefined,
  branchMap: BranchMap,
): string | number | null {
  if (!branchId || !branchMap) return null;
  return branchMap[branchId] ?? branchId;
}

export function resolveReplay(
  replay: Record<string, unknown> | null | undefined,
  maps: Pick<HubResolveMaps, 'iconMap' | 'branchMap'>,
): Record<string, unknown> | null {
  if (!replay) return null;
  const replayIconId = replay.iconId as number | null | undefined;
  return {
    ...replay,
    gotoBranchTitle: _resolveBranchTitle(replay.gotoBranchId as number | null | undefined, maps.branchMap),
    iconUrl: replayIconId ? (maps.iconMap[replayIconId] ?? null) : null,
  };
}
